import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  FormHelperText,
} from '@mui/material';
import { Close, Cancel } from '@mui/icons-material';
import { useCancelInvoice } from '../../hooks/useInvoices';
import { Invoice, CancellationReason } from '../../services/invoices';
import { getErrorInfo, ErrorInfo } from '../../services/api';
import ErrorMessage from '../ErrorMessage';

const cancellationReasons = [
  { value: 'client_requested', label: 'Client requested cancellation' },
  { value: 'duplicate', label: 'Duplicate invoice' },
  { value: 'incorrect_amount', label: 'Incorrect amount' },
  { value: 'service_not_delivered', label: 'Service not delivered' },
  { value: 'payment_arrangement', label: 'Payment arrangement changed' },
  { value: 'other', label: 'Other (specify below)' },
];

const cancelInvoiceSchema = z.object({
  reason: z.enum([
    'client_requested',
    'duplicate',
    'incorrect_amount',
    'service_not_delivered',
    'payment_arrangement',
    'other'
  ], { required_error: 'Please select a cancellation reason' }),
  note: z.string().max(500).optional(),
}).refine((data) => {
  // If reason is 'other', note is required
  if (data.reason === 'other' && (!data.note || data.note.trim() === '')) {
    return false;
  }
  return true;
}, {
  message: 'Please provide additional details when selecting "Other"',
  path: ['note'],
});

type CancelInvoiceFormData = z.infer<typeof cancelInvoiceSchema>;

interface CancelInvoiceModalProps {
  open: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function CancelInvoiceModal({
  open,
  invoice,
  onClose,
  onSuccess,
}: CancelInvoiceModalProps) {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const cancelInvoice = useCancelInvoice();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CancelInvoiceFormData>({
    resolver: zodResolver(cancelInvoiceSchema),
    defaultValues: {
      reason: undefined,
      note: '',
    },
  });

  const selectedReason = watch('reason');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      reset();
      setErrorInfo(null);
    }
  }, [open, reset]);

  const onSubmit = async (data: CancelInvoiceFormData) => {
    if (!invoice) return;
    setErrorInfo(null);

    try {
      await cancelInvoice.mutateAsync({
        id: invoice._id,
        reason: data.reason as CancellationReason,
        note: data.note,
      });

      reset();
      onClose();
      onSuccess?.();
    } catch (err: unknown) {
      const info = getErrorInfo(err);
      setErrorInfo({
        ...info,
        endpoint: `/invoices/${invoice._id}/cancel`,
        method: 'PATCH',
        payload: { reason: data.reason, note: data.note },
      });
    }
  };

  const handleClose = () => {
    reset();
    setErrorInfo(null);
    onClose();
  };

  if (!invoice) return null;

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice.currency || 'USD',
    }).format(cents / 100);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#DC2626',
              }}
            >
              <Cancel />
            </Box>
            <Typography variant="h6">Cancel Invoice</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {errorInfo && (
            <ErrorMessage
              error={errorInfo}
              userFriendlyMessage="Unable to cancel invoice. Please try again."
              onClose={() => setErrorInfo(null)}
            />
          )}

          {/* Invoice Info */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Invoice
                </Typography>
                <Typography variant="h6">
                  {invoice.invoiceNumber}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="body2" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(invoice.total)}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Client: {invoice.clientName}
            </Typography>
          </Box>

          <Box
            sx={{
              mb: 3,
              p: 2,
              border: '1px solid',
              borderColor: 'warning.main',
              borderRadius: 1,
              bgcolor: 'warning.light',
              color: 'warning.dark',
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              Cancelling an invoice cannot be undone. The invoice will be marked as cancelled
              and kept for your records.
            </Typography>
          </Box>

          {/* Reason Selection */}
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.reason} sx={{ mb: 2 }}>
                <InputLabel required>Cancellation Reason</InputLabel>
                <Select
                  {...field}
                  label="Cancellation Reason"
                  value={field.value || ''}
                >
                  {cancellationReasons.map((reason) => (
                    <MenuItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.reason && (
                  <FormHelperText>{errors.reason.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          {/* Additional Notes */}
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={selectedReason === 'other' ? 'Additional Details (Required)' : 'Additional Notes (Optional)'}
                multiline
                rows={3}
                fullWidth
                required={selectedReason === 'other'}
                placeholder="Add any additional context about why this invoice is being cancelled..."
                error={!!errors.note}
                helperText={errors.note?.message || 'This note will be saved with the cancelled invoice for your records.'}
              />
            )}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Keep Invoice
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Cancelling...' : 'Cancel Invoice'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
